import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSponsorshipApi } from '../../../Hooks/useSponsorshipApi';
import AdminLayout from '../../../Components/admin/shared/AdminLayout';

/**
 * Admin Sponsor Detail Page
 * Shows detailed sponsor information and allows editing
 */
const AdminSponsorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { sponsors } = useSponsorshipApi();
  const [sponsor, setSponsor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const loadSponsor = async () => {
      if (!id) return;

      try {
        const sponsorData = await sponsors.getById(id);
        setSponsor(sponsorData);
        setFormData(sponsorData);
      } catch (error) {
        console.error('Failed to load sponsor:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSponsor();
  }, [id, sponsors]);

  const handleEdit = () => {
    setEditing(true);
    setFormData(sponsor);
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData(sponsor);
  };

  const handleSave = async () => {
    try {
      const updatedSponsor = await sponsors.update(id, formData);
      setSponsor(updatedSponsor);
      setEditing(false);
    } catch (error) {
      console.error('Failed to update sponsor:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">
          <div className="loading-spinner">Loading sponsor details...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!sponsor) {
    return (
      <AdminLayout>
        <div className="admin-error">
          <h1>Sponsor Not Found</h1>
          <p>The sponsor you're looking for doesn't exist.</p>
          <button 
            className="admin-button admin-button--primary"
            onClick={() => navigate('/admin/sponsors')}
          >
            Back to Sponsors
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-sponsor-detail">
        <div className="admin-sponsor-detail__header">
          <div className="admin-sponsor-detail__title">
            <button 
              className="admin-button admin-button--ghost"
              onClick={() => navigate('/admin/sponsors')}
            >
              ← Back to Sponsors
            </button>
            <h1>{sponsor.name}</h1>
          </div>
          <div className="admin-sponsor-detail__actions">
            {editing ? (
              <>
                <button 
                  className="admin-button admin-button--secondary"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button 
                  className="admin-button admin-button--primary"
                  onClick={handleSave}
                >
                  Save Changes
                </button>
              </>
            ) : (
              <button 
                className="admin-button admin-button--primary"
                onClick={handleEdit}
              >
                Edit Sponsor
              </button>
            )}
          </div>
        </div>

        <div className="admin-sponsor-detail__content">
          <div className="admin-sponsor-detail__main">
            {/* Basic Information */}
            <div className="admin-card">
              <div className="admin-card__header">
                <h2>Basic Information</h2>
              </div>
              <div className="admin-card__content">
                <div className="admin-form">
                  <div className="admin-form__row">
                    <div className="admin-form__field">
                      <label>Name</label>
                      {editing ? (
                        <input
                          type="text"
                          value={formData.name || ''}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                        />
                      ) : (
                        <div className="admin-form__readonly">{sponsor.name}</div>
                      )}
                    </div>
                    <div className="admin-form__field">
                      <label>Status</label>
                      {editing ? (
                        <select
                          value={formData.is_active !== undefined ? formData.is_active.toString() : 'true'}
                          onChange={(e) => handleInputChange('is_active', e.target.value === 'true')}
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                      ) : (
                        <div className="admin-form__readonly">
                          <span className={`admin-badge admin-badge--${sponsor.is_active ? 'success' : 'error'}`}>
                            {sponsor.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="admin-form__row">
                    <div className="admin-form__field">
                      <label>Title</label>
                      {editing ? (
                        <input
                          type="text"
                          value={formData.title || ''}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                        />
                      ) : (
                        <div className="admin-form__readonly">{sponsor.title || 'N/A'}</div>
                      )}
                    </div>
                    <div className="admin-form__field">
                      <label>Industry Category</label>
                      {editing ? (
                        <select
                          value={formData.industry_category_id || ''}
                          onChange={(e) => handleInputChange('industry_category_id', e.target.value)}
                        >
                          <option value="">Select Category</option>
                          {/* TODO: Load categories from API */}
                          <option value="technology">Technology</option>
                          <option value="healthcare">Healthcare</option>
                          <option value="finance">Finance</option>
                        </select>
                      ) : (
                        <div className="admin-form__readonly">{sponsor.industry_category_id || 'N/A'}</div>
                      )}
                    </div>
                  </div>
                  <div className="admin-form__row">
                    <div className="admin-form__field admin-form__field--full">
                      <label>Description</label>
                      {editing ? (
                        <textarea
                          value={formData.description || ''}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          rows={4}
                        />
                      ) : (
                        <div className="admin-form__readonly">{sponsor.description || 'N/A'}</div>
                      )}
                    </div>
                  </div>
                  <div className="admin-form__row">
                    <div className="admin-form__field admin-form__field--full">
                      <label>Website URL</label>
                      {editing ? (
                        <input
                          type="url"
                          value={formData.website_url || ''}
                          onChange={(e) => handleInputChange('website_url', e.target.value)}
                        />
                      ) : (
                        <div className="admin-form__readonly">
                          {sponsor.website_url ? (
                            <a href={sponsor.website_url} target="_blank" rel="noopener noreferrer">
                              {sponsor.website_url}
                            </a>
                          ) : 'N/A'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Media Assets */}
            <div className="admin-card">
              <div className="admin-card__header">
                <h2>Media Assets</h2>
              </div>
              <div className="admin-card__content">
                {sponsor.media && sponsor.media.length > 0 ? (
                  <div className="admin-media-grid">
                    {sponsor.media.map((media) => (
                      <div key={media.id} className="admin-media-item">
                        <div className="admin-media-item__type">{media.type}</div>
                        <div className="admin-media-item__name">{media.url}</div>
                        <div className="admin-media-item__size">{(media.file_size / 1024).toFixed(1)} KB</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="admin-empty-state">
                    <p>No media assets uploaded</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="admin-sponsor-detail__sidebar">
            {/* Metadata */}
            <div className="admin-card">
              <div className="admin-card__header">
                <h2>Metadata</h2>
              </div>
              <div className="admin-card__content">
                <div className="admin-metadata">
                  <div className="admin-metadata__item">
                    <label>Sponsor ID</label>
                    <div>{sponsor.id}</div>
                  </div>
                  <div className="admin-metadata__item">
                    <label>Created</label>
                    <div>{new Date(sponsor.created_at).toLocaleString()}</div>
                  </div>
                  <div className="admin-metadata__item">
                    <label>Last Updated</label>
                    <div>{new Date(sponsor.updated_at).toLocaleString()}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="admin-card">
              <div className="admin-card__header">
                <h2>Contact Information</h2>
              </div>
              <div className="admin-card__content">
                <div className="admin-contact-info">
                  {sponsor.metadata?.contact_info ? (
                    <>
                      <div className="admin-contact-info__item">
                        <label>Contact Name</label>
                        <div>{sponsor.metadata.contact_info.name || 'N/A'}</div>
                      </div>
                      <div className="admin-contact-info__item">
                        <label>Email</label>
                        <div>{sponsor.metadata.contact_info.email || 'N/A'}</div>
                      </div>
                      <div className="admin-contact-info__item">
                        <label>Phone</label>
                        <div>{sponsor.metadata.contact_info.phone || 'N/A'}</div>
                      </div>
                    </>
                  ) : (
                    <div className="admin-empty-state">
                      <p>No contact information available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSponsorDetail;
